import { scriptToAddress, rawTransactionToHash, hexToBytes, serializeWitnessArgs, PERSONAL, toUint64Le, bytesToHex } from '@nervosnetwork/ckb-sdk-utils';
import resultFormatter from '@nervosnetwork/ckb-sdk-rpc/lib/resultFormatter';
import paramsFormatter from '@nervosnetwork/ckb-sdk-rpc/lib/paramsFormatter';
import blake2b from '@nervosnetwork/ckb-sdk-utils/lib/crypto/blake2b';
import blake160 from '@nervosnetwork/ckb-sdk-utils/lib/crypto/blake160';

const FlashSignerLockTestnet = {
    codeHash: '0x577a5e5930e2ecdd6200765f3442e6119dc99e87df474f22f13cab819c80b242',
    hashType: 'type',
    args: '',
};
const FlashSignerLockMainnet = {
    codeHash: '0x081dbffa88dab54ba426d231ca64eb760cea2fe9e16761a1da400da1b2cbe128',
    hashType: 'type',
    args: '',
};
const FlashsignerCellDepsTestnet = {
    depType: 'depGroup',
    outPoint: {
        index: '0x0',
        txHash: '0xb66776ff3244033fcd15312ae8b17d384c11bebbb923fce3bd896d89f4744d48',
    },
};
const FlashsignerCellDepsMainnet = {
    depType: 'depGroup',
    outPoint: {
        index: '0x0',
        txHash: '0x0f0c22372a05f3c5f47acb066c65f9bae86bdce043762310e50309cc5a77abd4',
    },
};
const PwLockTestnet = {
    hashType: 'type',
    codeHash: '0x58c5f491aba6d61678b7cf7edf4910b1f5e00ec0cde2f42e0abb4fd9aff25a63',
    args: '',
};
const PwLockMainnet = {
    hashType: 'type',
    codeHash: '0xbf43c3602455798c1a61a596e0d95278864c552fafe231c063b3fabf97a8febc',
    args: '',
};

class Config {
    static getFlashsignerURL() {
        return Config.flashsignerURL;
    }
    static setFlashsignerURL(url) {
        Config.flashsignerURL = url;
    }
    static setFlashsignerLock(lock) {
        Config.flashsignerLock = lock;
    }
    static getFlashsignerLock() {
        return Config.flashsignerLock;
    }
    static setCellDep(cellDep) {
        Config.cellDep = cellDep;
    }
    static getCellDep() {
        return Config.cellDep;
    }
    static setChainType(type) {
        Config.chainType = type;
        if (type === 'testnet') {
            Config.setFlashsignerLock(FlashSignerLockTestnet);
            Config.setFlashsignerURL('https://staging.flashsigner.work');
            Config.setPwcoreLock(PwLockTestnet);
            Config.setCellDep(FlashsignerCellDepsTestnet);
        }
        else {
            Config.setFlashsignerLock(FlashSignerLockMainnet);
            Config.setFlashsignerURL('https://flashsigner.com');
            Config.setPwcoreLock(PwLockMainnet);
            Config.setCellDep(FlashsignerCellDepsMainnet);
        }
    }
    static getChainType() {
        return Config.chainType;
    }
    static setPwcoreLock(lock) {
        Config.pwcoreLock = lock;
    }
    static getPwcoreLock() {
        return Config.pwcoreLock;
    }
}
Object.defineProperty(Config, "flashsignerURL", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 'https://flashsigner.com'
});
Object.defineProperty(Config, "flashsignerLock", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: FlashSignerLockMainnet
});
Object.defineProperty(Config, "chainType", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 'mainnet'
});
Object.defineProperty(Config, "pwcoreLock", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: PwLockMainnet
});
Object.defineProperty(Config, "cellDep", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: FlashsignerCellDepsMainnet
});

class UserRefuesedError extends Error {
    constructor(message = 'User Refuesed Error') {
        super(message);
        Object.defineProperty(this, "code", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 401
        });
        this.name = 'UserRefuesedError';
    }
}
class InconsistAddressError extends Error {
    constructor(message = 'NotFoundError') {
        super(message);
        Object.defineProperty(this, "code", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 402
        });
        this.name = 'NotFoundError';
    }
}
class InvalidParamsError extends Error {
    constructor(message = '') {
        super(`Invalid params: ${message}`);
        Object.defineProperty(this, "code", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 403
        });
        this.name = 'InvalidParamsError';
    }
}
class MissingParamsError extends Error {
    constructor(message = '') {
        super(`Missing params: ${message}`);
        Object.defineProperty(this, "code", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 404
        });
        this.name = 'MissingParamsError';
    }
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

/**
 * Check if we're required to add a port number.
 *
 * @see https://url.spec.whatwg.org/#default-port
 * @param {Number|String} port Port number we need to check
 * @param {String} protocol Protocol we need to check against.
 * @returns {Boolean} Is it a default port for the given protocol
 * @api private
 */
var requiresPort = function required(port, protocol) {
  protocol = protocol.split(':')[0];
  port = +port;

  if (!port) return false;

  switch (protocol) {
    case 'http':
    case 'ws':
    return port !== 80;

    case 'https':
    case 'wss':
    return port !== 443;

    case 'ftp':
    return port !== 21;

    case 'gopher':
    return port !== 70;

    case 'file':
    return false;
  }

  return port !== 0;
};

var querystringify$1 = {};

var has = Object.prototype.hasOwnProperty
  , undef;

/**
 * Decode a URI encoded string.
 *
 * @param {String} input The URI encoded string.
 * @returns {String|Null} The decoded string.
 * @api private
 */
function decode(input) {
  try {
    return decodeURIComponent(input.replace(/\+/g, ' '));
  } catch (e) {
    return null;
  }
}

/**
 * Attempts to encode a given input.
 *
 * @param {String} input The string that needs to be encoded.
 * @returns {String|Null} The encoded string.
 * @api private
 */
function encode(input) {
  try {
    return encodeURIComponent(input);
  } catch (e) {
    return null;
  }
}

/**
 * Simple query string parser.
 *
 * @param {String} query The query string that needs to be parsed.
 * @returns {Object}
 * @api public
 */
function querystring(query) {
  var parser = /([^=?#&]+)=?([^&]*)/g
    , result = {}
    , part;

  while (part = parser.exec(query)) {
    var key = decode(part[1])
      , value = decode(part[2]);

    //
    // Prevent overriding of existing properties. This ensures that build-in
    // methods like `toString` or __proto__ are not overriden by malicious
    // querystrings.
    //
    // In the case if failed decoding, we want to omit the key/value pairs
    // from the result.
    //
    if (key === null || value === null || key in result) continue;
    result[key] = value;
  }

  return result;
}

/**
 * Transform a query string to an object.
 *
 * @param {Object} obj Object that should be transformed.
 * @param {String} prefix Optional prefix.
 * @returns {String}
 * @api public
 */
function querystringify(obj, prefix) {
  prefix = prefix || '';

  var pairs = []
    , value
    , key;

  //
  // Optionally prefix with a '?' if needed
  //
  if ('string' !== typeof prefix) prefix = '?';

  for (key in obj) {
    if (has.call(obj, key)) {
      value = obj[key];

      //
      // Edge cases where we actually want to encode the value to an empty
      // string instead of the stringified value.
      //
      if (!value && (value === null || value === undef || isNaN(value))) {
        value = '';
      }

      key = encode(key);
      value = encode(value);

      //
      // If we failed to encode the strings, we should bail out as we don't
      // want to add invalid strings to the query.
      //
      if (key === null || value === null) continue;
      pairs.push(key +'='+ value);
    }
  }

  return pairs.length ? prefix + pairs.join('&') : '';
}

//
// Expose the module.
//
querystringify$1.stringify = querystringify;
querystringify$1.parse = querystring;

var required = requiresPort
  , qs = querystringify$1
  , slashes = /^[A-Za-z][A-Za-z0-9+-.]*:\/\//
  , protocolre = /^([a-z][a-z0-9.+-]*:)?(\/\/)?([\\/]+)?([\S\s]*)/i
  , windowsDriveLetter = /^[a-zA-Z]:/
  , whitespace = '[\\x09\\x0A\\x0B\\x0C\\x0D\\x20\\xA0\\u1680\\u180E\\u2000\\u2001\\u2002\\u2003\\u2004\\u2005\\u2006\\u2007\\u2008\\u2009\\u200A\\u202F\\u205F\\u3000\\u2028\\u2029\\uFEFF]'
  , left = new RegExp('^'+ whitespace +'+');

/**
 * Trim a given string.
 *
 * @param {String} str String to trim.
 * @public
 */
function trimLeft(str) {
  return (str ? str : '').toString().replace(left, '');
}

/**
 * These are the parse rules for the URL parser, it informs the parser
 * about:
 *
 * 0. The char it Needs to parse, if it's a string it should be done using
 *    indexOf, RegExp using exec and NaN means set as current value.
 * 1. The property we should set when parsing this value.
 * 2. Indication if it's backwards or forward parsing, when set as number it's
 *    the value of extra chars that should be split off.
 * 3. Inherit from location if non existing in the parser.
 * 4. `toLowerCase` the resulting value.
 */
var rules = [
  ['#', 'hash'],                        // Extract from the back.
  ['?', 'query'],                       // Extract from the back.
  function sanitize(address, url) {     // Sanitize what is left of the address
    return isSpecial(url.protocol) ? address.replace(/\\/g, '/') : address;
  },
  ['/', 'pathname'],                    // Extract from the back.
  ['@', 'auth', 1],                     // Extract from the front.
  [NaN, 'host', undefined, 1, 1],       // Set left over value.
  [/:(\d+)$/, 'port', undefined, 1],    // RegExp the back.
  [NaN, 'hostname', undefined, 1, 1]    // Set left over.
];

/**
 * These properties should not be copied or inherited from. This is only needed
 * for all non blob URL's as a blob URL does not include a hash, only the
 * origin.
 *
 * @type {Object}
 * @private
 */
var ignore = { hash: 1, query: 1 };

/**
 * The location object differs when your code is loaded through a normal page,
 * Worker or through a worker using a blob. And with the blobble begins the
 * trouble as the location object will contain the URL of the blob, not the
 * location of the page where our code is loaded in. The actual origin is
 * encoded in the `pathname` so we can thankfully generate a good "default"
 * location from it so we can generate proper relative URL's again.
 *
 * @param {Object|String} loc Optional default location object.
 * @returns {Object} lolcation object.
 * @public
 */
function lolcation(loc) {
  var globalVar;

  if (typeof window !== 'undefined') globalVar = window;
  else if (typeof commonjsGlobal !== 'undefined') globalVar = commonjsGlobal;
  else if (typeof self !== 'undefined') globalVar = self;
  else globalVar = {};

  var location = globalVar.location || {};
  loc = loc || location;

  var finaldestination = {}
    , type = typeof loc
    , key;

  if ('blob:' === loc.protocol) {
    finaldestination = new Url(unescape(loc.pathname), {});
  } else if ('string' === type) {
    finaldestination = new Url(loc, {});
    for (key in ignore) delete finaldestination[key];
  } else if ('object' === type) {
    for (key in loc) {
      if (key in ignore) continue;
      finaldestination[key] = loc[key];
    }

    if (finaldestination.slashes === undefined) {
      finaldestination.slashes = slashes.test(loc.href);
    }
  }

  return finaldestination;
}

/**
 * Check whether a protocol scheme is special.
 *
 * @param {String} The protocol scheme of the URL
 * @return {Boolean} `true` if the protocol scheme is special, else `false`
 * @private
 */
function isSpecial(scheme) {
  return (
    scheme === 'file:' ||
    scheme === 'ftp:' ||
    scheme === 'http:' ||
    scheme === 'https:' ||
    scheme === 'ws:' ||
    scheme === 'wss:'
  );
}

/**
 * @typedef ProtocolExtract
 * @type Object
 * @property {String} protocol Protocol matched in the URL, in lowercase.
 * @property {Boolean} slashes `true` if protocol is followed by "//", else `false`.
 * @property {String} rest Rest of the URL that is not part of the protocol.
 */

/**
 * Extract protocol information from a URL with/without double slash ("//").
 *
 * @param {String} address URL we want to extract from.
 * @param {Object} location
 * @return {ProtocolExtract} Extracted information.
 * @private
 */
function extractProtocol(address, location) {
  address = trimLeft(address);
  location = location || {};

  var match = protocolre.exec(address);
  var protocol = match[1] ? match[1].toLowerCase() : '';
  var forwardSlashes = !!match[2];
  var otherSlashes = !!match[3];
  var slashesCount = 0;
  var rest;

  if (forwardSlashes) {
    if (otherSlashes) {
      rest = match[2] + match[3] + match[4];
      slashesCount = match[2].length + match[3].length;
    } else {
      rest = match[2] + match[4];
      slashesCount = match[2].length;
    }
  } else {
    if (otherSlashes) {
      rest = match[3] + match[4];
      slashesCount = match[3].length;
    } else {
      rest = match[4];
    }
  }

  if (protocol === 'file:') {
    if (slashesCount >= 2) {
      rest = rest.slice(2);
    }
  } else if (isSpecial(protocol)) {
    rest = match[4];
  } else if (protocol) {
    if (forwardSlashes) {
      rest = rest.slice(2);
    }
  } else if (slashesCount >= 2 && isSpecial(location.protocol)) {
    rest = match[4];
  }

  return {
    protocol: protocol,
    slashes: forwardSlashes || isSpecial(protocol),
    slashesCount: slashesCount,
    rest: rest
  };
}

/**
 * Resolve a relative URL pathname against a base URL pathname.
 *
 * @param {String} relative Pathname of the relative URL.
 * @param {String} base Pathname of the base URL.
 * @return {String} Resolved pathname.
 * @private
 */
function resolve(relative, base) {
  if (relative === '') return base;

  var path = (base || '/').split('/').slice(0, -1).concat(relative.split('/'))
    , i = path.length
    , last = path[i - 1]
    , unshift = false
    , up = 0;

  while (i--) {
    if (path[i] === '.') {
      path.splice(i, 1);
    } else if (path[i] === '..') {
      path.splice(i, 1);
      up++;
    } else if (up) {
      if (i === 0) unshift = true;
      path.splice(i, 1);
      up--;
    }
  }

  if (unshift) path.unshift('');
  if (last === '.' || last === '..') path.push('');

  return path.join('/');
}

/**
 * The actual URL instance. Instead of returning an object we've opted-in to
 * create an actual constructor as it's much more memory efficient and
 * faster and it pleases my OCD.
 *
 * It is worth noting that we should not use `URL` as class name to prevent
 * clashes with the global URL instance that got introduced in browsers.
 *
 * @constructor
 * @param {String} address URL we want to parse.
 * @param {Object|String} [location] Location defaults for relative paths.
 * @param {Boolean|Function} [parser] Parser for the query string.
 * @private
 */
function Url(address, location, parser) {
  address = trimLeft(address);

  if (!(this instanceof Url)) {
    return new Url(address, location, parser);
  }

  var relative, extracted, parse, instruction, index, key
    , instructions = rules.slice()
    , type = typeof location
    , url = this
    , i = 0;

  //
  // The following if statements allows this module two have compatibility with
  // 2 different API:
  //
  // 1. Node.js's `url.parse` api which accepts a URL, boolean as arguments
  //    where the boolean indicates that the query string should also be parsed.
  //
  // 2. The `URL` interface of the browser which accepts a URL, object as
  //    arguments. The supplied object will be used as default values / fall-back
  //    for relative paths.
  //
  if ('object' !== type && 'string' !== type) {
    parser = location;
    location = null;
  }

  if (parser && 'function' !== typeof parser) parser = qs.parse;

  location = lolcation(location);

  //
  // Extract protocol information before running the instructions.
  //
  extracted = extractProtocol(address || '', location);
  relative = !extracted.protocol && !extracted.slashes;
  url.slashes = extracted.slashes || relative && location.slashes;
  url.protocol = extracted.protocol || location.protocol || '';
  address = extracted.rest;

  //
  // When the authority component is absent the URL starts with a path
  // component.
  //
  if (
    extracted.protocol === 'file:' && (
      extracted.slashesCount !== 2 || windowsDriveLetter.test(address)) ||
    (!extracted.slashes &&
      (extracted.protocol ||
        extracted.slashesCount < 2 ||
        !isSpecial(url.protocol)))
  ) {
    instructions[3] = [/(.*)/, 'pathname'];
  }

  for (; i < instructions.length; i++) {
    instruction = instructions[i];

    if (typeof instruction === 'function') {
      address = instruction(address, url);
      continue;
    }

    parse = instruction[0];
    key = instruction[1];

    if (parse !== parse) {
      url[key] = address;
    } else if ('string' === typeof parse) {
      if (~(index = address.indexOf(parse))) {
        if ('number' === typeof instruction[2]) {
          url[key] = address.slice(0, index);
          address = address.slice(index + instruction[2]);
        } else {
          url[key] = address.slice(index);
          address = address.slice(0, index);
        }
      }
    } else if ((index = parse.exec(address))) {
      url[key] = index[1];
      address = address.slice(0, index.index);
    }

    url[key] = url[key] || (
      relative && instruction[3] ? location[key] || '' : ''
    );

    //
    // Hostname, host and protocol should be lowercased so they can be used to
    // create a proper `origin`.
    //
    if (instruction[4]) url[key] = url[key].toLowerCase();
  }

  //
  // Also parse the supplied query string in to an object. If we're supplied
  // with a custom parser as function use that instead of the default build-in
  // parser.
  //
  if (parser) url.query = parser(url.query);

  //
  // If the URL is relative, resolve the pathname against the base URL.
  //
  if (
      relative
    && location.slashes
    && url.pathname.charAt(0) !== '/'
    && (url.pathname !== '' || location.pathname !== '')
  ) {
    url.pathname = resolve(url.pathname, location.pathname);
  }

  //
  // Default to a / for pathname if none exists. This normalizes the URL
  // to always have a /
  //
  if (url.pathname.charAt(0) !== '/' && isSpecial(url.protocol)) {
    url.pathname = '/' + url.pathname;
  }

  //
  // We should not add port numbers if they are already the default port number
  // for a given protocol. As the host also contains the port number we're going
  // override it with the hostname which contains no port number.
  //
  if (!required(url.port, url.protocol)) {
    url.host = url.hostname;
    url.port = '';
  }

  //
  // Parse down the `auth` for the username and password.
  //
  url.username = url.password = '';
  if (url.auth) {
    instruction = url.auth.split(':');
    url.username = instruction[0];
    url.password = instruction[1] || '';
  }

  url.origin = url.protocol !== 'file:' && isSpecial(url.protocol) && url.host
    ? url.protocol +'//'+ url.host
    : 'null';

  //
  // The href is just the compiled result.
  //
  url.href = url.toString();
}

/**
 * This is convenience method for changing properties in the URL instance to
 * insure that they all propagate correctly.
 *
 * @param {String} part          Property we need to adjust.
 * @param {Mixed} value          The newly assigned value.
 * @param {Boolean|Function} fn  When setting the query, it will be the function
 *                               used to parse the query.
 *                               When setting the protocol, double slash will be
 *                               removed from the final url if it is true.
 * @returns {URL} URL instance for chaining.
 * @public
 */
function set(part, value, fn) {
  var url = this;

  switch (part) {
    case 'query':
      if ('string' === typeof value && value.length) {
        value = (fn || qs.parse)(value);
      }

      url[part] = value;
      break;

    case 'port':
      url[part] = value;

      if (!required(value, url.protocol)) {
        url.host = url.hostname;
        url[part] = '';
      } else if (value) {
        url.host = url.hostname +':'+ value;
      }

      break;

    case 'hostname':
      url[part] = value;

      if (url.port) value += ':'+ url.port;
      url.host = value;
      break;

    case 'host':
      url[part] = value;

      if (/:\d+$/.test(value)) {
        value = value.split(':');
        url.port = value.pop();
        url.hostname = value.join(':');
      } else {
        url.hostname = value;
        url.port = '';
      }

      break;

    case 'protocol':
      url.protocol = value.toLowerCase();
      url.slashes = !fn;
      break;

    case 'pathname':
    case 'hash':
      if (value) {
        var char = part === 'pathname' ? '/' : '#';
        url[part] = value.charAt(0) !== char ? char + value : value;
      } else {
        url[part] = value;
      }
      break;

    case 'username':
    case 'password':
      url[part] = encodeURIComponent(value);
      break;

    case 'auth':
      var splits = value.split(':');
      url.username = splits[0];
      url.password = splits.length === 2 ? splits[1] : '';
  }

  for (var i = 0; i < rules.length; i++) {
    var ins = rules[i];

    if (ins[4]) url[ins[1]] = url[ins[1]].toLowerCase();
  }

  url.auth = url.password ? url.username +':'+ url.password : url.username;

  url.origin = url.protocol !== 'file:' && isSpecial(url.protocol) && url.host
    ? url.protocol +'//'+ url.host
    : 'null';

  url.href = url.toString();

  return url;
}

/**
 * Transform the properties back in to a valid and full URL string.
 *
 * @param {Function} stringify Optional query stringify function.
 * @returns {String} Compiled version of the URL.
 * @public
 */
function toString(stringify) {
  if (!stringify || 'function' !== typeof stringify) stringify = qs.stringify;

  var query
    , url = this
    , protocol = url.protocol;

  if (protocol && protocol.charAt(protocol.length - 1) !== ':') protocol += ':';

  var result =
    protocol +
    ((url.protocol && url.slashes) || isSpecial(url.protocol) ? '//' : '');

  if (url.username) {
    result += url.username;
    if (url.password) result += ':'+ url.password;
    result += '@';
  } else if (url.password) {
    result += ':'+ url.password;
    result += '@';
  }

  result += url.host + url.pathname;

  query = 'object' === typeof url.query ? stringify(url.query) : url.query;
  if (query) result += '?' !== query.charAt(0) ? '?'+ query : query;

  if (url.hash) result += url.hash;

  return result;
}

Url.prototype = { set: set, toString: toString };

//
// Expose the URL parser and some additional properties that might be useful for
// others or testing.
//
Url.extractProtocol = extractProtocol;
Url.location = lolcation;
Url.trimLeft = trimLeft;
Url.qs = qs;

var urlParse = Url;

var FlashsignerAction;
(function (FlashsignerAction) {
    FlashsignerAction["SignMessage"] = "sign-message";
    FlashsignerAction["SignRawMessage"] = "sign-raw-message";
    FlashsignerAction["SignTransaction"] = "sign-transaction";
    FlashsignerAction["Login"] = "login";
    FlashsignerAction["TransferMnft"] = "transfer-mnft";
})(FlashsignerAction || (FlashsignerAction = {}));
const FLASHSIGNER_DATA_KEY = 'flashsigner_data';

const generateLoginURL = (successUrl, options = {}) => {
    const { name, locale, logo, phoneNumber, id, extra, failUrl } = options;
    const url = new urlParse(`${Config.getFlashsignerURL()}/connect`, true);
    const { query } = url;
    const surl = new urlParse(successUrl, true);
    surl.query.action = FlashsignerAction.Login;
    if (extra) {
        surl.query.extra = JSON.stringify(extra);
    }
    query.success_url = surl.toString();
    if (name) {
        query.dapp_name = name;
    }
    if (locale) {
        query.locale = locale;
    }
    if (logo) {
        query.dapp_logo = logo;
    }
    if (id) {
        query.id = id;
    }
    if (phoneNumber) {
        query.phone_number = phoneNumber;
    }
    if (failUrl) {
        query.fail_url = failUrl;
    }
    const href = url.toString();
    return href;
};
const loginWithRedirect = (successUrl, options = {}) => {
    const { isReplace } = options, rest = __rest(options, ["isReplace"]);
    const href = generateLoginURL(successUrl, rest);
    if (isReplace) {
        window.location.replace(href);
    }
    else {
        window.location.href = href;
    }
    return href;
};
function getLoginResult(data, id, extra) {
    const chainType = Config.getChainType();
    const result = {
        message: data.message,
        signature: data.sig,
        address: scriptToAddress({
            hashType: data.lock.hash_type,
            codeHash: data.lock.code_hash,
            args: data.lock.args,
        }, chainType === 'mainnet'),
        pubkey: data.sig.slice(0, 520),
    };
    if (id) {
        result.id = id;
    }
    if (extra) {
        result.extra = extra;
    }
    return result;
}

const generateTransferMnftURL = (successUrl, options) => {
    const { name, locale, logo, id, extra, failUrl, classId, issuerId, toAddress, tokenId, fromAddress, } = options;
    const url = new urlParse(`${Config.getFlashsignerURL()}/transfer-mnft`, true);
    const { query } = url;
    const surl = new urlParse(successUrl, true);
    surl.query.action = FlashsignerAction.TransferMnft;
    if (extra) {
        surl.query.extra = JSON.stringify(extra);
    }
    query.success_url = surl.toString();
    query.class_id = classId;
    query.issuer_id = issuerId;
    query.token_id = tokenId;
    query.to_address = toAddress;
    query.from_address = fromAddress;
    if (name) {
        query.dapp_name = name;
    }
    if (locale) {
        query.locale = locale;
    }
    if (logo) {
        query.dapp_logo = logo;
    }
    if (id) {
        query.id = id;
    }
    if (failUrl) {
        query.fail_url = failUrl;
    }
    return url.toString();
};
const generateTransferCotaNftURL = (successUrl, options) => {
    const { name, locale, logo, id, extra, failUrl, tokenIndex, cotaId, toAddress, fromAddress, } = options;
    const url = new urlParse(`${Config.getFlashsignerURL()}/transfer-cnft`, true);
    const { query } = url;
    const surl = new urlParse(successUrl, true);
    surl.query.action = FlashsignerAction.TransferMnft;
    if (extra) {
        surl.query.extra = JSON.stringify(extra);
    }
    query.success_url = surl.toString();
    query.class_id = cotaId;
    query.token_id = tokenIndex.toString();
    query.to_address = toAddress;
    query.from_address = fromAddress;
    if (name) {
        query.dapp_name = name;
    }
    if (locale) {
        query.locale = locale;
    }
    if (logo) {
        query.dapp_logo = logo;
    }
    if (id) {
        query.id = id;
    }
    if (failUrl) {
        query.fail_url = failUrl;
    }
    return url.toString();
};
const transferMnftWithRedirect = (successUrl, options) => {
    const { isReplace } = options, rest = __rest(options, ["isReplace"]);
    const href = generateTransferMnftURL(successUrl, rest);
    if (isReplace) {
        window.location.replace(href);
    }
    else {
        window.location.href = href;
    }
};
const transferCotaNftWithRedirect = (successUrl, options) => {
    const { isReplace } = options, rest = __rest(options, ["isReplace"]);
    const href = generateTransferCotaNftURL(successUrl, rest);
    if (isReplace) {
        window.location.replace(href);
    }
    else {
        window.location.href = href;
    }
};
function getTransferMnftResult(data, id, extra) {
    const chainType = Config.getChainType();
    const result = {
        transaction: data.tx,
        address: scriptToAddress({
            hashType: data.lock.hash_type,
            codeHash: data.lock.code_hash,
            args: data.lock.args,
        }, chainType === 'mainnet'),
    };
    if (id) {
        result.id = id;
    }
    if (extra) {
        result.extra = extra;
    }
    return result;
}

const generateSignMessageURL = (successUrl, options = { message: '' }) => {
    const { name, locale, logo, isRaw, id, extra, failUrl, message } = options;
    const url = new urlParse(`${Config.getFlashsignerURL()}/sign-message`, true);
    const { query } = url;
    const surl = new urlParse(successUrl, true);
    // surl.searchParams.set(
    //   'action',
    //   isRaw ? FlashsignerAction.SignRawMessage : FlashsignerAction.SignMessage
    // )
    surl.query.action = isRaw
        ? FlashsignerAction.SignRawMessage
        : FlashsignerAction.SignMessage;
    if (extra) {
        // surl.searchParams.set('extra', JSON.stringify(extra))
        surl.query.extra = JSON.stringify(extra);
    }
    // searchParams.set('success_url', surl.toString())
    query.success_url = surl.toString();
    if (message === '') {
        throw new MissingParamsError('message');
    }
    // searchParams.set('message', message)
    query.message = message;
    if (name) {
        // searchParams.set('dapp_name', name)
        query.dapp_name = name;
    }
    if (locale) {
        // searchParams.set('locale', locale)
        query.locale = locale;
    }
    if (logo) {
        // searchParams.set('dapp_logo', logo)
        query.dapp_logo = logo;
    }
    if (id) {
        // searchParams.set('id', id)
        query.id = id;
    }
    if (isRaw) {
        // searchParams.set('is_raw', Boolean(isRaw).toString())
        query.is_raw = Boolean(isRaw).toString();
    }
    if (failUrl) {
        // searchParams.set('fail_url', failUrl)
        query.fail_url = failUrl;
    }
    const href = url.toString();
    return href;
};
const RSA_KEY_SIZE = 2048;
const appendFlashsignerCelldeps = (cellDeps) => {
    const flCellDep = Config.getCellDep();
    const newCellDeps = cellDeps.map(resultFormatter.toCellDep);
    if (newCellDeps.find((dep) => { var _a, _b; return ((_a = dep.outPoint) === null || _a === void 0 ? void 0 : _a.txHash) === ((_b = flCellDep.outPoint) === null || _b === void 0 ? void 0 : _b.txHash); })) {
        return newCellDeps;
    }
    newCellDeps.push(flCellDep);
    return newCellDeps;
};
const transactionToMessage = (transaction) => {
    const cellDeps = appendFlashsignerCelldeps(transaction.cell_deps);
    const tx = {
        version: transaction.version,
        cellDeps,
        headerDeps: transaction.header_deps,
        inputs: transaction.inputs.map(resultFormatter.toInput),
        outputs: transaction.outputs.map(resultFormatter.toOutput),
        outputsData: transaction.outputs_data,
        witnesses: [],
    };
    tx.witnesses = tx.inputs.map((_, i) => i > 0 ? '0x' : { lock: '', inputType: '', outputType: '' });
    const witnessGroup = tx.witnesses;
    if (!witnessGroup.length) {
        throw new Error('WitnessGroup cannot be empty');
    }
    if (typeof witnessGroup[0] !== 'object') {
        throw new Error('The first witness in the group should be type of WitnessArgs');
    }
    const transactionHash = rawTransactionToHash(tx);
    // len(smt_type + pub_key_e + pub_key_n + signature)
    const lockLength = 2 + 8 + (RSA_KEY_SIZE / 4) * 2;
    const emptyWitness = Object.assign(Object.assign({}, witnessGroup[0]), { lock: `0x${'0'.repeat(lockLength)}` });
    const serializedEmptyWitnessBytes = hexToBytes(serializeWitnessArgs(emptyWitness));
    const serializedEmptyWitnessSize = serializedEmptyWitnessBytes.length;
    const hash = blake2b(32, null, null, PERSONAL);
    hash.update(hexToBytes(transactionHash));
    hash.update(hexToBytes(toUint64Le(`0x${serializedEmptyWitnessSize.toString(16)}`)));
    hash.update(serializedEmptyWitnessBytes);
    witnessGroup.slice(1).forEach((w) => {
        const bytes = hexToBytes(typeof w === 'string' ? w : serializeWitnessArgs(w));
        hash.update(hexToBytes(toUint64Le(`0x${bytes.length.toString(16)}`)));
        hash.update(bytes);
    });
    const message = `${hash.digest('hex')}`;
    return message;
};
const appendSignatureToTransaction = (transaction, signedMessage) => {
    const cellDeps = appendFlashsignerCelldeps(transaction.cell_deps);
    const tx = {
        version: transaction.version,
        cellDeps,
        headerDeps: transaction.header_deps,
        inputs: transaction.inputs.map(resultFormatter.toInput),
        outputs: transaction.outputs.map(resultFormatter.toOutput),
        outputsData: transaction.outputs_data,
        witnesses: [],
    };
    tx.witnesses = tx.inputs.map((_, i) => i > 0 ? '0x' : { lock: '', inputType: '', outputType: '' });
    const witnessGroup = tx.witnesses;
    if (!witnessGroup.length) {
        throw new Error('WitnessGroup cannot be empty');
    }
    if (typeof witnessGroup[0] !== 'object') {
        throw new Error('The first witness in the group should be type of WitnessArgs');
    }
    const transactionHash = rawTransactionToHash(tx);
    // len(smt_type + pub_key_e + pub_key_n + signature)
    const lockLength = 2 + 8 + (RSA_KEY_SIZE / 4) * 2;
    const emptyWitness = Object.assign(Object.assign({}, witnessGroup[0]), { lock: `0x${'0'.repeat(lockLength)}` });
    const serializedEmptyWitnessBytes = hexToBytes(serializeWitnessArgs(emptyWitness));
    const serializedEmptyWitnessSize = serializedEmptyWitnessBytes.length;
    const hash = blake2b(32, null, null, PERSONAL);
    hash.update(hexToBytes(transactionHash));
    hash.update(hexToBytes(toUint64Le(`0x${serializedEmptyWitnessSize.toString(16)}`)));
    hash.update(serializedEmptyWitnessBytes);
    witnessGroup.slice(1).forEach((w) => {
        const bytes = hexToBytes(typeof w === 'string' ? w : serializeWitnessArgs(w));
        hash.update(hexToBytes(toUint64Le(`0x${bytes.length.toString(16)}`)));
        hash.update(bytes);
    });
    const rsaType = '01' ;
    emptyWitness.lock = `0x${rsaType}${signedMessage}`;
    const signedWitnesses = [
        serializeWitnessArgs(emptyWitness),
        ...witnessGroup.slice(1),
    ];
    return paramsFormatter.toRawTransaction(Object.assign(Object.assign({}, tx), { witnesses: signedWitnesses.map((witness) => typeof witness === 'string' ? witness : serializeWitnessArgs(witness)) }));
};
function getSignTxResult(data, id, extra) {
    const chainType = Config.getChainType();
    if (extra == null) {
        throw new Error('transaction is required');
    }
    const { tx: txToSign } = extra, restExtra = __rest(extra, ["tx"]);
    const signedTx = appendSignatureToTransaction(txToSign, data.sig);
    const result = {
        transaction: signedTx,
        address: scriptToAddress({
            hashType: data.lock.hash_type,
            codeHash: data.lock.code_hash,
            args: data.lock.args,
        }, chainType === 'mainnet'),
    };
    if (id) {
        result.id = id;
    }
    if (restExtra && Object.keys(restExtra).length !== 0) {
        result.extra = restExtra;
    }
    return result;
}
const generateSignTransactionURL = (successUrl, options) => {
    const { tx, extra } = options, rest = __rest(options, ["tx", "extra"]);
    const href = generateSignMessageURL(successUrl, Object.assign(Object.assign({}, rest), { message: transactionToMessage(tx), extra: Object.assign(Object.assign({}, extra), { tx }) }));
    const url = new urlParse(href, true);
    // const surl = new URL(url.searchParams.get('success_url')!)
    const surl = new urlParse(url.query.success_url, true);
    // surl.searchParams.set('action', FlashsignerAction.SignTransaction)
    surl.query.action = FlashsignerAction.SignTransaction;
    // url.searchParams.set('success_url', surl.toString())
    url.query.success_url = surl.toString();
    return url.toString();
};
const signTransactionWithRedirect = (successUrl, options) => {
    const { isReplace } = options, rest = __rest(options, ["isReplace"]);
    const href = generateSignTransactionURL(successUrl, rest);
    if (isReplace) {
        window.location.replace(href);
    }
    else {
        window.location.href = href;
    }
    return href;
};
const signMessageWithRedirect = (successUrl, options = { message: '' }) => {
    const { isReplace } = options, rest = __rest(options, ["isReplace"]);
    const href = generateSignMessageURL(successUrl, rest);
    if (isReplace) {
        window.location.replace(href);
    }
    else {
        window.location.href = href;
    }
    return href;
};

function getResultFromURL(uriOrOptions, opts) {
    const uri = typeof uriOrOptions === 'string' ? uriOrOptions : window.location.href;
    const options = typeof uriOrOptions === 'string' ? opts : uriOrOptions;
    if (options == null) {
        throw new Error('options is required');
    }
    const { onLogin, onTransferMnft, onSignMessage, onSignRawMessage, onError, onSignTransaction, } = options;
    const url = new urlParse(uri, true);
    const { query } = url;
    const action = query.action;
    if (action === null) {
        throw new Error('Missing dapp action');
    }
    const data = query[FLASHSIGNER_DATA_KEY];
    const { extra, id } = query;
    const parsedExtra = extra ? JSON.parse(extra) : undefined;
    if (data == null) {
        throw new Error('Missing dapp data');
    }
    const parsedData = JSON.parse(data);
    if (parsedData.code !== 200) {
        switch (parsedData.code) {
            case 401:
                onError === null || onError === void 0 ? void 0 : onError(new UserRefuesedError(), action, parsedExtra);
                break;
            case 402:
                onError === null || onError === void 0 ? void 0 : onError(new InconsistAddressError(), action, parsedExtra);
                break;
            case 403:
                onError === null || onError === void 0 ? void 0 : onError(new InvalidParamsError(parsedData.error), action, parsedExtra);
                break;
            case 404:
                onError === null || onError === void 0 ? void 0 : onError(new MissingParamsError(parsedData.error), action, parsedExtra);
                break;
        }
        return;
    }
    switch (action) {
        case FlashsignerAction.Login:
            onLogin(getLoginResult(parsedData.result, id, parsedExtra));
            break;
        case FlashsignerAction.SignMessage:
            onSignMessage === null || onSignMessage === void 0 ? void 0 : onSignMessage(getLoginResult(parsedData.result, id, parsedExtra));
            break;
        case FlashsignerAction.SignRawMessage:
            onSignRawMessage === null || onSignRawMessage === void 0 ? void 0 : onSignRawMessage(getLoginResult(parsedData.result, id, parsedExtra));
            break;
        case FlashsignerAction.SignTransaction:
            onSignTransaction === null || onSignTransaction === void 0 ? void 0 : onSignTransaction(getSignTxResult(parsedData.result, id, parsedExtra));
            break;
        case FlashsignerAction.TransferMnft:
            onTransferMnft === null || onTransferMnft === void 0 ? void 0 : onTransferMnft(getTransferMnftResult(parsedData.result, id, parsedExtra));
            break;
        default:
            throw new Error('invalid dapp action');
    }
}

const generateFlashsignerLockScript = (pubkey) => {
    const pubkeyHash = `${bytesToHex(blake160(`0x${pubkey}`))}`;
    const lock = Config.getFlashsignerLock();
    return Object.assign(Object.assign({}, lock), { args: pubkeyHash });
};
const generateFlashsignerAddress = (pubkey) => scriptToAddress(generateFlashsignerLockScript(pubkey), Config.getChainType() === 'mainnet');

export { Config, FLASHSIGNER_DATA_KEY, FlashSignerLockMainnet, FlashSignerLockTestnet, FlashsignerAction, FlashsignerCellDepsMainnet, FlashsignerCellDepsTestnet, InconsistAddressError, InvalidParamsError, MissingParamsError, PwLockMainnet, PwLockTestnet, RSA_KEY_SIZE, UserRefuesedError, appendSignatureToTransaction, generateFlashsignerAddress, generateFlashsignerLockScript, generateLoginURL, generateSignMessageURL, generateSignTransactionURL, generateTransferCotaNftURL, generateTransferMnftURL, getLoginResult, getResultFromURL, getSignTxResult, getTransferMnftResult, loginWithRedirect, signMessageWithRedirect, signTransactionWithRedirect, transactionToMessage, transferCotaNftWithRedirect, transferMnftWithRedirect };
